<script setup lang="ts">
import { useEventListener, useElementBounding, type MaybeElement } from '@vueuse/core';
import { ref, onMounted, nextTick, watch, type Ref } from 'vue';
import { useScrollParent } from '.';

const props = withDefaults(
  defineProps<{
    top?: boolean,
    finished?: boolean,
    error?: boolean,
    loading: boolean,
    loadingText?: string,
    finishText?: string,
    errorText?: string,
    offset?: number,
  }>(), {
    loadingText: '加载中...',
    finishText: '没有更多了',
    errorText: '加载失败，请重试',
    offset: 10
  });

const emit = defineEmits<{
  (e: 'load'): void;
  (e: 'load-prev'): void;
  (e: 'update:loading', value: boolean): void;
}>();

onMounted(() => {
  emit('load');
});

const root = ref<HTMLElement>();
const topPlaceholder = ref<HTMLElement>();
const bottomPlaceholder = ref<HTMLElement>();
const scrollParent = useScrollParent(root);

const loadingPrev = ref(false);
const loadingNext = ref(props.loading);

const oldScrollHeight = ref(0);

const check = () => {
  nextTick(() => {
    if (props.loading) {
      return;
    }

    if (!scrollParent.value) {
      return;
    }

    const { top: scrollTop, bottom: scrollBottom } = useElementBounding(scrollParent as Ref<MaybeElement>);
    const { top: topPlaceholderTop } = useElementBounding(topPlaceholder);
    const { bottom: bottomPlaceholderBottom } = useElementBounding(bottomPlaceholder);

    const isReachTop = scrollTop.value - topPlaceholderTop.value <= props.offset;
    const isReachBottom = bottomPlaceholderBottom.value - scrollBottom.value <= props.offset;

    if (isReachBottom && !loadingNext.value) {
      if (props.finished || props.error) {
        return;
      }
      loadingNext.value = true;
      emit('update:loading', true);
      emit('load');
      return;
    }
    if (isReachTop && !loadingPrev.value) {
      if (props.top || props.error) {
        return;
      }
      oldScrollHeight.value = scrollParent.value.scrollHeight;

      loadingPrev.value = true;
      emit('update:loading', true);
      emit('load-prev');
    }
  });
};

useEventListener(scrollParent, 'scroll', check);

const restoreScrollPosition = async () => {
  await nextTick();
  if (scrollParent.value) {
    scrollParent.value.scrollTop = scrollParent.value.scrollHeight - oldScrollHeight.value;
  }

};

watch(() => props.loading, async (loading) => {
  if (!loading) {
    if (loadingPrev.value) {
      await restoreScrollPosition();
      loadingPrev.value = false;
    }
    loadingNext.value = false;

    check();
  }
});
</script>

<template>
  <div ref="root">
    <div :class="$style.text" v-if="loadingPrev">{{ loadingText }}</div>
    <div ref="topPlaceholder" :class="$style.placeholder" />
    <slot></slot>
    <div ref="bottomPlaceholder" :class="$style.placeholder" />
    <div :class="$style.text" v-if="loadingNext">{{ loadingText }}</div>
    <div :class="$style.text" v-if="props.finished">{{ finishText }}</div>
    <div :class="$style.text" v-if="props.error">{{ errorText }}</div>
  </div>
</template>

<style module>
.placeholder {
  pointer-events: none;
  height: 5px;
}

.text {
  text-align: center;
  padding: 10px;
}
</style>